import { BookOpen, BookText, ClipboardCheck, Compass, GraduationCap, Library } from 'lucide-react';

import type { Guide } from '../types';

/**
 * LMS guides. The LMS module is open to all staff (no nav role gate), so these
 * guides carry no `roles`. NOTE: the LMS "Guides" tab is a *learning* content
 * type — distinct from this Help & Guides hub.
 */
export const LMS_GUIDES: Guide[] = [
  {
    slug: 'lms-overview',
    title: 'Learning at a glance',
    module: 'lms',
    summary: 'Courses, the materials library, guides and assessments.',
    icon: GraduationCap,
    body: `**Learning (LMS)** is where the team learns and proves it — structured courses,
a library of resources, and assessments.

\`\`\`
 COURSES ──▶ LESSONS (material · video · guide · link)
                 │
   MATERIALS  ◀──┘  the same lessons, browsable across all courses
                 │
 ASSESSMENTS ────┘  test understanding · scores recorded · pass/fail
\`\`\`

### What's here
- **Courses** — structured learning paths made of lessons.
- **Materials** — every lesson, browsable in one library and filterable by type.
- **Guides** — the how-to / playbook lessons (a learning content type — *not* this
  Help & Guides hub).
- **Assessments** — quizzes/tests with a passing score and recorded attempts.
- **Education Zone** — a curated learning space (being built out).

### Who sees it
Learning is open to all staff — everyone can browse courses and materials.`,
  },
  {
    slug: 'lms-courses',
    title: 'Courses',
    module: 'lms',
    summary: 'Structured learning paths built from lessons.',
    icon: BookOpen,
    body: `A **Course** is a structured learning path — a set of **lessons** grouped
toward a goal (onboarding, a skill, a process).

### How to use it
- Browse courses and open one to work through its lessons.
- Each lesson has a **kind**: **material**, **video**, **guide** or **link**.
- Create or edit a course and its lessons when you're building training.

### Why it's here
Knowledge shouldn't live in scattered docs and people's heads. Courses package it
into a clear path so the team can get up to speed consistently.`,
  },
  {
    slug: 'lms-materials',
    title: 'Materials',
    module: 'lms',
    summary: 'Every lesson in one library — filterable by type.',
    icon: Library,
    body: `**Materials** is the library view: every lesson across all courses in one
place, so you can find a resource without hunting through courses.

### How to use it
- Search and **filter by type** — material, video, guide or link.
- Open a lesson to read/watch it.

### Why it's here
Sometimes you just need *that one doc or video*. The library makes the whole body
of learning content browsable and searchable in a single spot.`,
  },
  {
    slug: 'lms-guides',
    title: 'Guides (learning)',
    module: 'lms',
    summary: 'The how-to / playbook lessons — a learning content type.',
    icon: BookText,
    body: `The LMS **Guides** view is the **library filtered to the *guide* lesson type** —
the how-tos and playbooks the team writes for itself.

> Not to be confused with **this Help & Guides hub** (product help). LMS Guides
> are *your* internal playbooks; this hub explains how the app works.

### How to use it
- Browse the guide-type lessons; open one to read it.
- Author a guide by adding a lesson of kind **guide** to a course.

### Why it's here
A dedicated home for "how we do things here" — process docs and playbooks kept
alongside the rest of your learning content.`,
  },
  {
    slug: 'lms-assessments',
    title: 'Assessments',
    module: 'lms',
    summary: 'Test understanding — scores recorded, pass/fail decided server-side.',
    icon: ClipboardCheck,
    body: `**Assessments** check that learning landed — a test tied to a course, with a
**passing score**.

### How to use it
1. Set up an assessment with a **max score** and a **passing score**.
2. **Record a score** for someone (an *attempt*) — **pass/fail is decided
   server-side** from the passing score, so it's consistent.
3. Review attempts to see who's passed.

### Why it's here
Training is only done when it sticks. Assessments give an objective, recorded
check — useful for compliance, onboarding sign-off and skill verification.`,
  },
  {
    slug: 'lms-education-zone',
    title: 'Education Zone',
    module: 'lms',
    summary: 'A curated learning space (being built out).',
    icon: Compass,
    body: `The **Education Zone** is a curated space meant to bring the most useful
learning resources together in one welcoming place.

> **Heads-up:** this area is still being built out — the courses, materials and
> assessments above are where learning lives today.

### Why it's here
A friendly front door to learning — somewhere to surface featured courses and
recommended reading as the library grows.`,
  },
];
